import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import EmployeesDroppable from 'af-root/scenes/Company/ScheduleBoard/DailyView/EmployeesDroppable';

import { NotificationStatusEnum } from '@acceligentllc/shared/enums/notificationStatus';
import { ScheduleBoardContext } from '@acceligentllc/shared/enums/scheduleBoardContext';
import { ScheduleBoardProperty } from '@acceligentllc/shared/enums/scheduleBoardProperty';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import type { RootState } from 'af-reducers';

import * as ScheduleBoardUtil from 'af-utils/scheduleBoard.util';

import { AVAILABLE_EMPLOYEE_STATUS } from 'ab-common/constants/employee';

import StatusIcon from 'af-components/StatusIcon';

interface OwnProps {
	dueDate: string;
	hasReason?: boolean;
	available: boolean;
	groupTitle: Nullable<string>;
	groupId: number;
	isDeleted: boolean;
	isDragAndDropDisabled: boolean;
	hasPermissionsToEditScheduleBoard: boolean;
	hasPermissionsToSendNotifications: boolean;
	openNotifyModal?: (groupId: number) => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const DEFAULT_EMPLOYEE_IDS = [];

const EmployeeGroup: React.FC<Props> = (props) => {
	const {
		dueDate,
		isDeleted,
		available = true,
		groupTitle = AVAILABLE_EMPLOYEE_STATUS,
		groupId,
		hasReason = false,
		isShowNotifyAvailableEmployeesDisabled,
		isDragAndDropDisabled,
		hasPermissionsToEditScheduleBoard,
		hasPermissionsToSendNotifications,
		disabled,
		openNotifyModal,
		employeeIds,
		employees,
		availableNotificationStatuses,
	} = props;

	const assignableEmployeeIds = React.useMemo(
		() => employeeIds.filter(
			(_empId) => employees?.[_empId]?.account?.assignableToWorkOrder
		),
		[employeeIds, employees]);

	const hasArrow = React.useMemo(() => assignableEmployeeIds.some((_id) => employees?.[_id]?.isMatched), [assignableEmployeeIds, employees]);

	const employeesNotified = React.useMemo(
		() => !assignableEmployeeIds.some((_id) => {
			const emailStatus = availableNotificationStatuses?.[_id]?.emailStatus !== NotificationStatusEnum.OPENED;
			const smsStatus = availableNotificationStatuses?.[_id]?.smsStatus !== NotificationStatusEnum.OPENED;
			return emailStatus && smsStatus;
		}
		), [assignableEmployeeIds, availableNotificationStatuses]);

	const _employeeIds = React.useMemo(
		() => assignableEmployeeIds.filter((_empId) => employees?.[_empId]?.showOnScheduleBoard ?? false)
		, [assignableEmployeeIds, employees]);

	const openNotificationModal = React.useCallback(() => {
		openNotifyModal?.(groupId);
	}, [groupId, openNotifyModal]);

	if (isDeleted && employeeIds.length === 0) {
		return null;
	}

	const notificationsAvailable = available
		&& !isShowNotifyAvailableEmployeesDisabled
		&& hasPermissionsToSendNotifications;

	return (
		<div className="schedule-board-toolbar-employees-group" key={groupId}>
			<div className="schedule-board-arrow-container">
				<div className={`schedule-board-arrow ${hasArrow ? '--visible' : '--invisible'}`} />
			</div>
			<div className="group-title">
				{
					available ?
						<img src="/images/elements/ic_state_complete.svg" /> :
						<img src="/images/elements/ic_state_incomplete.svg" />
				}
				<span>{groupTitle}</span>
				{
					notificationsAvailable &&
					<span className="notification-status">
						<StatusIcon
							firstRevision={false}
							isSmall={true}
							notificationsEnabled={!isShowNotifyAvailableEmployeesDisabled}
							notificationStatus={employeesNotified ? NotificationStatusEnum.OPENED : NotificationStatusEnum.DELIVERED}
							onClick={openNotificationModal}
							status={WorkOrderStatus.PUBLISHED}
						/>
					</span>
				}
			</div>
			<hr />
			<EmployeesDroppable
				available={available}
				disabled={disabled || isDeleted}
				draggablePrefix={ScheduleBoardContext.TOOLBAR}
				droppableId={ScheduleBoardUtil.generateDroppableId(ScheduleBoardContext.TOOLBAR, ScheduleBoardProperty.EMPLOYEE, dueDate, `${groupId}`, undefined, available ? 'available' : 'unavailable')}
				dueDate={dueDate}
				employeeIds={_employeeIds}
				hasPermissionsToEditScheduleBoard={hasPermissionsToEditScheduleBoard}
				hasReason={hasReason}
				isDragAndDropDisabled={isDragAndDropDisabled}
				isToolbar={true}
			/>
		</div>
	);
};

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { dueDate, available, groupId } = ownProps;
	const { employees, workOrdersByDateDictionary } = state.scheduleBoard;
	const { company } = state.company;

	const availability = ScheduleBoardUtil.getAvailabilityLabel(available);
	const workOrders = workOrdersByDateDictionary?.[dueDate];
	const employeeIds = workOrders?.toolbarEmployees?.[availability]?.[groupId] ?? DEFAULT_EMPLOYEE_IDS;
	const availableNotificationStatuses = workOrders?.availableNotificationStatuses ?? null;

	return {
		employeeIds,
		employees,
		availableNotificationStatuses,
		disabled: !state.scheduleBoard.draggedEmployeeId,
		isShowNotifyAvailableEmployeesDisabled: !company?.notification?.isEnabled ?? false,
	};
}

const connector = connect(mapStateToProps);

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(EmployeeGroup);
